import { useContext, useEffect, useState } from "react";
import {
  Table,
  DatePicker,
  Button,
  Space,
  Popconfirm,
  Tooltip,
  message,
  Modal,
} from "antd";
import AuthContext from "../../context/AuthContext";
import {
  ClockCircleOutlined,
  CheckOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const LeadTodosList = ({ leadId, refreshTodos, setRefreshTodos }) => {
  const { authTokens, user } = useContext(AuthContext);
  const accessToken = authTokens.accessToken;

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTodoId, setSelectedTodoId] = useState(null);
  const [newDueDate, setNewDueDate] = useState(null);
  const [dueDateModalVisible, setDueDateModalVisible] = useState(false);

  useEffect(() => {
    const fetchTodos = async () => {
      // Fetch todos for the given leadId
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + accessToken);

      const requestOptions = {
        method: "GET",
        headers: headers,
        credentials: "include",
      };

      const url = `${process.env.REACT_APP_BACKEND}/v1/todos/${leadId}`;

      setLoading(true);
      let response = await fetch(url, requestOptions);
      const data = await response.json();

      if (!data.error) {
        setTodos(data);
      }
      setLoading(false);
    };

    fetchTodos();
  }, [accessToken, leadId, refreshTodos]);

  const disabledDate = (current) => {
    // Can not select days before today and today
    return current && current < dayjs().startOf("day");
  };

  // Function to check if due date is past due
  const isPastDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    return due < today;
  };

  const columns = [
    {
      title: "Task",
      dataIndex: "task",
      key: "task",
      align: "center",
      ellipsis: true,
      fixed: "left",
      render: (task, record) => (
        <Tooltip placement="topLeft" title={task}>
          <span
            className={`${isPastDue(record.due_date) ? "text-danger" : ""}`}
          >
            {task}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      align: "center",
      defaultSortOrder: "ascend",
      sorter: (a, b) => new Date(a.due_date) - new Date(b.due_date),
      render: (dueDate) => (
        <span className={`${isPastDue(dueDate) ? "text-danger" : ""}`}>
          {new Date(dueDate).toLocaleDateString("en-US", {
            hour12: false,
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) =>
        <Space size="middle">
          {user.userID === record.user_id && (
            <Popconfirm
              title="Complete this task?"
              onConfirm={() => handleCompleteTask(record.id)}
            >
              <Button shape="circle" icon={<CheckOutlined />} />
            </Popconfirm>
          )}
          <Tooltip title="change due date">
            <Button
              type="dashed"
              shape="circle"
              icon={<ClockCircleOutlined />}
              onClick={() => handleOpenModal(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this task?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="danger" shape="circle" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
    },
    {
      title: "Task Owner",
      dataIndex: "todo_owner",
      key: "todo_owner",
      align: "center",
      width: 150,
    },
  ].filter((item) => !item.hidden);

  const handleOpenModal = (todoId) => {
    setDueDateModalVisible(true);
    setSelectedTodoId(todoId);
  };

  const handleUpdateDueDateConfirm = async () => {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + accessToken);

    const adjustedDueDate = dayjs(newDueDate).endOf("day").toDate();
    let payload = {
      dueDate: adjustedDueDate,
    };

    const requestOptions = {
      method: "PATCH",
      headers: headers,
      credentials: "include",
      body: JSON.stringify(payload),
    };

    const url = `${process.env.REACT_APP_BACKEND}/v1/todos/snooze/${selectedTodoId}`;

    try {
      let response = await fetch(url, requestOptions);
      if (response.ok) {
        message.success("Task due date updated");
        // Refresh todos
      } else {
        message.error("Failed to update task due date");
      }
    } catch (error) {
      console.error("Error updating task due date", error);
      message.error("Error updating task due date");
    } finally {
      setSelectedTodoId(null);
      setDueDateModalVisible(false);
      setRefreshTodos((prevState) => !prevState);
    }
  };

  const handleCompleteTask = async (todoId) => {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + accessToken);
    const requestOptions = {
      method: "PATCH",
      headers: headers,
      credentials: "include",
    };

    const url = `${process.env.REACT_APP_BACKEND}/v1/todos/complete/${todoId}`;
    try {
      let response = await fetch(url, requestOptions);
      if (response.ok) {
        message.success("Task completed");
        setRefreshTodos((prevState) => !prevState);
      } else {
        message.error("Failed to mark the task completed");
      }
    } catch (err) {
      console.log(err);
      message.error("Error marking task completed");
    }
  };

  const handleDelete = async (todoId) => {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + accessToken);
    const requestOptions = {
      method: "DELETE",
      headers: headers,
      credentials: "include",
    };

    const url = `${process.env.REACT_APP_BACKEND}/v1/todos/${todoId}`;
    try {
      let response = await fetch(url, requestOptions);
      if (response.ok) {
        message.success("Task deleted");
        setRefreshTodos((prevState) => !prevState);
      } else {
        message.error("Failed to delete the task");
      }
    } catch (err) {
      console.log(err);
      message.error("Error deleting task");
    }
  };

  return todos.length === 0 || loading ? (
    <></>
  ) : (
    <div>
      <Table
        columns={columns}
        dataSource={todos}
        rowKey="id"
        scroll={{ x: 1200, y: "100%" }}
        loading={loading}
      />
      <Modal
        title="Select a new due date"
        open={dueDateModalVisible}
        onOk={handleUpdateDueDateConfirm}
        centered={true}
        closeIcon={false}
        onCancel={() => setDueDateModalVisible(false)}
        className="text-center"
      >
        <DatePicker
          disabledDate={disabledDate}
          allowClear={false}
          value={newDueDate}
          size="large"
          onChange={(date) => setNewDueDate(date)}
        />
      </Modal>
    </div>
  );
};

export default LeadTodosList;
